import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Users, Wallet, ChevronRight, PhoneCall, Mail, MapPin, Building2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function HomePage() {
  // Auto-login flow: if a valid session cookie/token exists, send the user
  // straight to their dashboard instead of showing the marketing homepage.
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);
  return (
    <div className="min-h-screen bg-[#09090B] text-white flex flex-col" data-testid="home-page">
      {/* Navigation bar */}
      <header className="border-b border-border bg-[#09090B]/80 backdrop-blur-sm sticky top-0 z-30">
        <nav className="max-w-6xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="home-brand-link">
            <div className="w-9 h-9 rounded-full border-2 border-volt flex items-center justify-center bg-black">
              <span className="font-display font-bold text-volt text-[10px] leading-none tracking-tighter">24-7</span>
            </div>
            <span className="font-display text-lg font-bold tracking-tight group-hover:text-volt transition-colors">
              Samtale<span className="text-volt">24-7</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Button
              asChild
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold"
              data-testid="home-cta-nav"
            >
              <Link to="/free-group-joined">Kom i gang - gratis <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-volt/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-volt/5 blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-6 relative">
          <div className="max-w-3xl space-y-5 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-volt/40 bg-volt/10 px-3 py-1 text-xs font-medium text-volt">
              <span className="w-1.5 h-1.5 rounded-full bg-volt animate-pulse" /> Anonymt · Digitalt · Trygt
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Hvad er <span className="text-volt">samtale24-7.dk</span>?
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Trænger du til nogen at snakke med — eller vil du bare lytte?
            </p>
            <p className="text-base text-muted-foreground leading-relaxed max-w-2xl">
              Samtale24-7.dk er et trygt, digitalt og anonymt rum, hvor ligesindede kan mødes.
              Vi formidler samtaler mellem ligesindede — når du har brug for det, døgnet rundt.
            </p>
          </div>
        </div>

        {/* Full-width hero image — matches your original homepage design */}
        <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
          <div className="relative rounded-xl overflow-hidden border border-border shadow-[0_20px_80px_-20px_rgba(217,255,0,0.15)] animate-fade-up" style={{ animationDelay: '120ms' }}>
            <img
              src="https://customer-assets-lqy194kg.emergentagent.net/job_unified-video-chat/artifacts/fa2wf7oc_davinci_i_want_to_create_an_image___one_person_to_the_left.png"
              alt="To personer taler i telefon med hinanden via Samtale24-7"
              className="w-full h-auto object-cover"
              loading="eager"
              data-testid="home-hero-image"
            />
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-8 pb-16 md:pb-20 relative">
          <div className="flex flex-wrap gap-3 justify-center animate-fade-up" style={{ animationDelay: '200ms' }}>
            <Button
              asChild
              size="lg"
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-xl transition-all font-semibold"
              data-testid="home-cta-hero"
            >
              <Link to="/free-group-joined">Prøv anonymt og gratis <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-border hover:bg-[#18181B] hover:text-white"
            >
              <a href="#hvordan">Sådan fungerer det</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Feature bullets — the reassurance grid */}
      <section className="border-t border-border bg-[#0d0d0f]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Users,
              title: 'Ligesindede finder hinanden',
              body: 'Samtale24-7.dk formidler samtaler mellem ligesindede — så du altid er blandt folk, der forstår dig.',
            },
            {
              icon: Wallet,
              title: 'Ingen teleafgifter',
              body: 'Du bruger internettet. Ingen ekstra opkaldsgebyrer — bare taletid, når du har lyst.',
            },
            {
              icon: Shield,
              title: 'Ægte anonymitet',
              body: 'Når du er koblet på en samtalegruppe, kan ingen se det. Ingen kan høre dig, medmindre du selv deltager.',
            },
          ].map((f) => (
            <div key={f.title} className="rounded-lg border border-border bg-[#18181B] p-6 hover:border-volt/40 transition-colors">
              <div className="w-10 h-10 rounded-md bg-volt/10 text-volt flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Extra assurance strip */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 space-y-4 text-sm">
          <p className="text-white/90"><span className="text-volt font-semibold">Du bestemmer selv</span>, om du vil vise dit kaldenavn. Du kan også oprette dine egne samtalegrupper.</p>
          <p className="text-muted-foreground">Hvis du kan huske <span className="text-white">0059</span> fra gammel tid — så vil du være tryg ved Samtale24-7.dk.</p>
          <p className="text-xs italic text-yellow-500/80 border-l-2 border-yellow-500/50 pl-4 mt-4">
            Bemærk: Samtale24-7.dk er ikke en terapilinje.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="hvordan" className="border-t border-border bg-[#0d0d0f]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-widest text-volt mb-3">Sådan gør du</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Sådan fungerer <span className="text-volt">Samtale24-7.dk</span>
            </h2>
          </div>
          <ol className="space-y-6">
            {[
              {
                title: 'Først kobler du dig gratis til en samtalegruppe',
                body: 'Ingen registrering. Ingen kortoplysninger. Du får 30 minutters anonym taletid med det samme.',
              },
              {
                title: 'Når du har brugt dine 30 gratis minutter',
                body: 'Registrer dig og betal — kun 48 kr / første måned. Vi beder kun om din e-mail, kaldenavn og adgangskode. Vi kræver ingen personlige informationer.',
              },
              {
                title: 'Nu vælger du selv dine samtalegrupper',
                body: 'Tal om hvad som helst, når som helst — lige når det passer dig. Ingen samtaleudgifter, kun det faste månedlige beløb.',
              },
            ].map((step, i) => (
              <li key={step.title} className="flex gap-5 rounded-lg border border-border bg-[#18181B] p-6">
                <div className="shrink-0 w-11 h-11 rounded-full bg-volt text-black font-display font-black text-lg flex items-center justify-center shadow-lg">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-volt/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-center space-y-6 relative">
          <div className="text-xs uppercase tracking-widest text-volt">Kom i gang</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
            Prøv <span className="text-volt">anonymt og gratis</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            30 minutters gratis taletid venter på dig. Ingen kort. Ingen personlige oplysninger. Bare start.
          </p>
          <div className="flex justify-center gap-3 flex-wrap pt-2">
            <Button
              asChild
              size="lg"
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-xl transition-all font-semibold text-base px-8"
              data-testid="home-cta-final"
            >
              <Link to="/free-group-joined">
                Start nu — det er gratis
                <ChevronRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-[#050506] mt-auto">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 grid md:grid-cols-4 gap-6 text-sm">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full border-2 border-volt flex items-center justify-center bg-black">
                <span className="font-display font-bold text-volt text-[9px] leading-none tracking-tighter">24-7</span>
              </div>
              <span className="font-display font-bold tracking-tight">
                Samtale<span className="text-volt">24-7</span>.dk
              </span>
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed max-w-sm">
              Et trygt, digitalt og anonymt rum, hvor ligesindede kan mødes — når du har brug for det, døgnet rundt.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-volt mb-3 font-medium">Kontakt</div>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-volt shrink-0" /> 4270 Høng</li>
              <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-volt shrink-0" /> <a href="mailto:info@samtale24-7.dk" className="hover:text-volt transition-colors">info@samtale24-7.dk</a></li>
              <li className="flex items-center gap-2"><PhoneCall className="w-3.5 h-3.5 text-volt shrink-0" /> Hotline: 552 18 552</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-volt mb-3 font-medium">Selskab</div>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-volt shrink-0" /> CVR: DK4163 0930</li>
              <li><Link to="/login" className="hover:text-volt transition-colors">Log ind</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border/50">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 text-xs text-muted-foreground flex justify-between flex-wrap gap-2">
            <span>© 2026 Samtale24-7.dk</span>
            <span>Anonymt · Digitalt · Trygt</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
